import React, { useEffect, useRef, useState } from 'react';
import type { GenerationResult } from '../types';
import {
  exportJsonLdScript,
  exportJsonLdRaw,
  exportMicrodata,
  exportRdfa,
  exportMarkdownReport,
  exportJsonReport,
  triggerDownload,
  copyToClipboard,
  suggestedFilename,
} from '../lib/export';

interface ExportMenuProps {
  generations: GenerationResult[];
  graph: Record<string, unknown>;
  format: 'jsonld' | 'microdata' | 'rdfa';
  code: string;
  sourceUrl: string;
}

export const ExportMenu: React.FC<ExportMenuProps> = ({ generations, graph, format, code }) => {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(id);
  }, [toast]);

  const close = () => setOpen(false);

  const handleCopyCurrent = async () => {
    const ok = await copyToClipboard(code);
    setToast(ok ? `${formatLabel(format)} copied` : 'Copy failed — try Download');
    close();
  };

  const handleCopyJson = async () => {
    const ok = await copyToClipboard(exportJsonLdRaw(graph));
    setToast(ok ? 'JSON-LD copied' : 'Copy failed');
    close();
  };

  const handleDownloadJsonLd = () => {
    triggerDownload(
      suggestedFilename(generations, 'jsonld.html'),
      'text/html',
      exportJsonLdScript(graph),
    );
    close();
  };

  const handleDownloadJson = () => {
    triggerDownload(
      suggestedFilename(generations, 'json'),
      'application/ld+json',
      exportJsonLdRaw(graph),
    );
    close();
  };

  const handleDownloadMicrodata = () => {
    triggerDownload(
      suggestedFilename(generations, 'microdata.html'),
      'text/html',
      exportMicrodata(graph),
    );
    close();
  };

  const handleDownloadRdfa = () => {
    triggerDownload(
      suggestedFilename(generations, 'rdfa.html'),
      'text/html',
      exportRdfa(graph),
    );
    close();
  };

  const handleDownloadMd = () => {
    triggerDownload(
      suggestedFilename(generations, 'md'),
      'text/markdown',
      exportMarkdownReport(generations, graph),
    );
    close();
  };

  const handleDownloadFullJson = () => {
    triggerDownload(
      suggestedFilename(generations, 'report.json'),
      'application/json',
      exportJsonReport(generations),
    );
    close();
  };

  const handlePrint = () => {
    close();
    setTimeout(() => window.print(), 50);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }} className="export-menu no-print">
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Export
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden style={{ marginLeft: 2 }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 280,
            background: 'var(--paper)',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            boxShadow: 'var(--shadow-2)',
            padding: '6px 0',
            zIndex: 50,
          }}
        >
          <SectionLabel>Copy</SectionLabel>
          <MenuItem
            label={`Copy current format (${formatLabel(format)})`}
            onClick={handleCopyCurrent}
          />
          <MenuItem label="Copy JSON-LD (raw JSON)" hint="Without <script> wrapper" onClick={handleCopyJson} />
          <Divider />
          <SectionLabel>Download</SectionLabel>
          <MenuItem label="JSON-LD (.html)" hint="Drop into <head>" onClick={handleDownloadJsonLd} />
          <MenuItem label="JSON-LD raw (.json)" hint=".jsonld" onClick={handleDownloadJson} />
          <MenuItem label="Microdata (.html)" onClick={handleDownloadMicrodata} />
          <MenuItem label="RDFa (.html)" onClick={handleDownloadRdfa} />
          <Divider />
          <SectionLabel>Bundle</SectionLabel>
          <MenuItem label="Markdown report (.md)" hint="All formats + notes" onClick={handleDownloadMd} />
          <MenuItem label="Full JSON report (.json)" hint="Including field notes" onClick={handleDownloadFullJson} />
          <Divider />
          <MenuItem label="Print / Save as PDF" hint="Cmd+P" onClick={handlePrint} />
        </div>
      )}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            background: 'var(--ink)',
            color: 'var(--paper)',
            padding: '8px 14px',
            borderRadius: 4,
            fontSize: 13,
            whiteSpace: 'nowrap',
            zIndex: 60,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
};

function formatLabel(f: 'jsonld' | 'microdata' | 'rdfa'): string {
  return f === 'jsonld' ? 'JSON-LD' : f === 'microdata' ? 'Microdata' : 'RDFa';
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      padding: '6px 16px 4px',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--ink-4)',
    }}
  >
    {children}
  </div>
);

const MenuItem: React.FC<{ label: string; hint?: string; onClick: () => void }> = ({ label, hint, onClick }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    style={{
      display: 'block',
      width: '100%',
      textAlign: 'left',
      background: 'transparent',
      border: 0,
      padding: '9px 16px',
      cursor: 'pointer',
      font: 'inherit',
      color: 'var(--ink)',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-2)')}
    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
  >
    <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
    {hint && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{hint}</div>}
  </button>
);

const Divider: React.FC = () => (
  <div role="separator" style={{ height: 1, background: 'var(--rule)', margin: '6px 0' }} />
);
